import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storeTokens } from './cognito';
import { completeLogin, OAuthError } from './oauth';
import { useAuth } from './useAuth';
import styles from './LoginPage.module.css';

/**
 * OAuth redirect target (/auth/callback). Exchanges the one-time code for
 * tokens, then lands on the page the user was heading to (default /work).
 */
export function AuthCallbackPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Codes are single-use: StrictMode's double effect must not exchange twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const { tokens, returnTo } = await completeLogin(search);
        storeTokens(tokens);
        await refresh();
        navigate(returnTo, { replace: true });
      } catch (e) {
        setError(e instanceof OAuthError ? e.message : 'Sign-in could not be completed. Please try again.');
      }
    })();
  }, [search, navigate, refresh]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <img className={styles.logo} src="/dnm-wordmark-color.svg" alt={t('auth.authCallbackPage.logoAlt')} />
          <div className={styles.title}>{t('auth.authCallbackPage.title')}</div>
        </div>
        {error ? (
          <>
            <div className={styles.error} role="alert">
              {error}
            </div>
            <Link className={styles.backLink} to="/login" replace>
              {t('auth.authCallbackPage.backToSignIn')}
            </Link>
          </>
        ) : (
          <div className={styles.status}>{t('auth.authCallbackPage.signingIn')}</div>
        )}
      </div>
    </div>
  );
}
